import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  CircularProgress,
  Pagination,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Snackbar,
  DialogContentText,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search,
  Visibility,
  ContentCopy,
  Group,
  Euro,
  Link as LinkIcon,
  Business,
  CloudUpload,
  Description,
  Lock,
  VisibilityOff,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import { useDebounce } from "../hooks/useDebounce";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import api from "../api";
import { cleanWhatsAppNumber, validateWhatsAppNumber, displayWhatsAppNumber } from '../utils/phoneUtils';
import { MuiTelInput } from 'mui-tel-input';

interface Partner {
  _id: string;
  partnerCode: string;
  name: string;
  phoneNumber: string;
  IBAN: string;
  referredFee: number;
  referralLink: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PaginatedResponse {
  data: Partner[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface PartnerFormData {
  name: string;
  phoneNumber: string;
  IBAN: string;
  isActive?: boolean;
}

const partnerSchema = yup.object({
  name: yup.string().required("Nome é obrigatório"),
  phoneNumber: yup
    .string()
    .required("Número WhatsApp é obrigatório")
    .test('whatsapp-validation', 'Número WhatsApp deve ser um número internacional válido (ex: +351 912 345 678, +1 555 123 4567)', (value) => validateWhatsAppNumber(value || '')),
  IBAN: yup
    .string()
    .required("IBAN é obrigatório")
    .transform((value) => (value ? value.replace(/\s/g, "") : value)) // Remove spaces for validation
    .matches(
      /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/,
      "IBAN inválido"
    ),
  isActive: yup.boolean().optional(),
}) as yup.ObjectSchema<PartnerFormData>;

export default function PartnersPage() {
  const { user } = useAuth();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Password management states
  const [partnerPassword, setPartnerPassword] = useState<string | null>(null);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Debounce search term to avoid excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Form for partner creation/editing
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<PartnerFormData>({
    resolver: yupResolver(partnerSchema),
    defaultValues: {
      name: "",
      phoneNumber: "",
      IBAN: "",
      isActive: true,
    },
  });

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const response = await api.get("/partners", {
        params: {
          page,
          limit: 10,
          search: debouncedSearchTerm || undefined,
        },
      });
      const data: PaginatedResponse = response.data;
      setPartners(data.data);
      setTotalPages(data.pagination.pages);
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao carregar parceiros");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, [page, debouncedSearchTerm]); // Use debounced search term

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setPage(1); // Reset to first page when searching
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
  };

  const fetchPartnerPassword = async (partnerId: string) => {
    setLoadingPassword(true);
    try {
      const response = await api.get(`/partners/${partnerId}/password`);
      setPartnerPassword(response.data.data.password);
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Erro ao carregar senha do parceiro"
      );
    } finally {
      setLoadingPassword(false);
    }
  };

  const handleViewPartner = (partner: Partner) => {
    setSelectedPartner(partner);
    setPartnerPassword(null);
    setShowPassword(false);
    setShowDetails(true);
  };

  const handleEditPartner = (partner: Partner) => {
    setSelectedPartner(partner);
    setValue("name", partner.name);
    setValue("phoneNumber", displayWhatsAppNumber(partner.phoneNumber));
    setValue("IBAN", formatIBAN(partner.IBAN));
    setValue("isActive", partner.isActive);
    setIsEditing(true);
    setShowPartnerDialog(true);
  };

  const handleDeletePartner = (partner: Partner) => {
    setSelectedPartner(partner);
    setShowDeleteDialog(true);
  };

  const handleCreatePartner = () => {
    setSelectedPartner(null);
    setIsEditing(false);
    reset();
    setShowPartnerDialog(true);
  };

  const onSubmitPartner = async (data: PartnerFormData) => {
    setSubmitting(true);
    setError(null);

    try {
      if (isEditing && selectedPartner) {
        // Update existing partner
        await api.put(`/partners/${selectedPartner._id}`, {
          name: data.name,
          phoneNumber: cleanWhatsAppNumber(data.phoneNumber),
          IBAN: data.IBAN.replace(/\s/g, ''), // Remove spaces before sending
          isActive: data.isActive,
        });
      } else {
        // Create new partner
        await api.post("/partners", {
          name: data.name,
          phoneNumber: cleanWhatsAppNumber(data.phoneNumber),
          IBAN: data.IBAN.replace(/\s/g, ''), // Remove spaces before sending
        });
      }

      setShowPartnerDialog(false);
      reset();
      fetchPartners(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.message || `Erro ao ${isEditing ? 'atualizar' : 'criar'} parceiro`);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeletePartner = async () => {
    if (!selectedPartner) return;
    
    setDeleting(true);
    setError(null);

    try {
      await api.delete(`/partners/${selectedPartner._id}`);
      setShowDeleteDialog(false);
      fetchPartners(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao deletar parceiro");
    } finally {
      setDeleting(false);
    }
  };

  const handleShowPassword = () => {
    if (!partnerPassword && selectedPartner) {
      fetchPartnerPassword(selectedPartner._id);
    }
    setShowPassword(!showPassword);
  };

  const formatIBAN = (iban: string) => {
    return iban.replace(/(.{4})/g, "$1 ").trim();
  };

  // IBAN mask function
  const formatIBANField = (value: string) => {
    // Remove all non-alphanumeric characters
    const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    
    // Apply IBAN format with spaces every 4 characters
    const formatted = cleaned.replace(/(.{4})/g, '$1 ').trim();
    
    return formatted;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: (theme) => theme.palette.grey[100],
        py: 3,
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={3}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Group sx={{ fontSize: 32, color: "primary.main" }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Gestão de Parceiros
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreatePartner}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Novo Parceiro
            </Button>
          </Box>

          {/* Search */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              placeholder="Buscar por nome, código ou IBAN..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{ minWidth: 300 }}
            />
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Partners Table */}
          <Paper elevation={2}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Telefone</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>IBAN</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Taxa</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Criado em</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : partners.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Typography color="text.secondary">
                          Nenhum parceiro encontrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    partners.map((partner) => (
                      <TableRow key={partner._id} hover>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", fontWeight: 600 }}
                          >
                            {partner.partnerCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 500 }}>
                            {partner.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {displayWhatsAppNumber(partner.phoneNumber)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {formatIBAN(partner.IBAN)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Euro sx={{ fontSize: 16 }} />
                            <Typography>{partner.referredFee}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={partner.isActive ? "Ativo" : "Inativo"}
                            color={partner.isActive ? "success" : "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(partner.createdAt).toLocaleDateString(
                              "pt-BR"
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="Ver detalhes">
                              <IconButton
                                size="small"
                                onClick={() => handleViewPartner(partner)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copiar link de indicação">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  copyToClipboard(partner.referralLink)
                                }
                              >
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar parceiro">
                              <IconButton
                                size="small"
                                onClick={() => handleEditPartner(partner)}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir parceiro">
                              <IconButton
                                size="small"
                                onClick={() => handleDeletePartner(partner)}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, newPage) => setPage(newPage)}
                  color="primary"
                />
              </Box>
            )}
          </Paper>
        </Stack>
      </Container>

      {/* Partner Details Dialog */}
      <Dialog
        open={showDetails}
        onClose={() => setShowDetails(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedPartner && (
          <>
            <DialogTitle>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Business />
                <Typography variant="h6">Detalhes do Parceiro</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={3} sx={{ mt: 1 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Informações Básicas
                    </Typography>
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography color="text.secondary">Código:</Typography>
                        <Typography
                          sx={{ fontFamily: "monospace", fontWeight: 600 }}
                        >
                          {selectedPartner.partnerCode}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography color="text.secondary">Nome:</Typography>
                        <Typography sx={{ fontWeight: 500 }}>
                          {selectedPartner.name}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2">
                          <strong>Telefone:</strong> {displayWhatsAppNumber(selectedPartner.phoneNumber)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography color="text.secondary">IBAN:</Typography>
                        <Typography sx={{ fontFamily: "monospace" }}>
                          {formatIBAN(selectedPartner.IBAN)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography color="text.secondary">
                          Taxa de Indicação:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Euro sx={{ fontSize: 16 }} />
                          <Typography sx={{ fontWeight: 600 }}>
                            {selectedPartner.referredFee}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Link de Indicação
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "grey.50",
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <LinkIcon sx={{ color: "primary.main" }} />
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          wordBreak: "break-all",
                          flexGrow: 1,
                        }}
                      >
                        {selectedPartner.referralLink}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          copyToClipboard(selectedPartner.referralLink)
                        }
                      >
                        <ContentCopy />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Lock />
                      Senha de Acesso
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "grey.50",
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {showPassword ? (
                        <>
                          <Lock sx={{ color: "primary.main" }} />
                          {loadingPassword ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Typography
                              variant="body1"
                              sx={{
                                fontFamily: "monospace",
                                fontWeight: 600,
                                fontSize: "1.1rem",
                                letterSpacing: "0.1em",
                                flexGrow: 1,
                              }}
                            >
                              {partnerPassword || "••••••••"}
                            </Typography>
                          )}
                          {partnerPassword && (
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(partnerPassword)}
                            >
                              <ContentCopy />
                            </IconButton>
                          )}
                          <IconButton size="small" onClick={handleShowPassword}>
                            <VisibilityOff />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Lock sx={{ color: "text.secondary" }} />
                          <Typography
                            variant="body1"
                            sx={{
                              color: "text.secondary",
                              flexGrow: 1,
                            }}
                          >
                            ••••••••
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleShowPassword}
                            startIcon={<Visibility />}
                          >
                            Mostrar Senha
                          </Button>
                        </>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      Use esta senha para que o parceiro possa acessar seu
                      painel de tracking.
                    </Typography>
                  </CardContent>
                </Card>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowDetails(false)}>Fechar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Partner Create/Edit Dialog */}
      <Dialog
        open={showPartnerDialog}
        onClose={() => setShowPartnerDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Business />
            <Typography variant="h6">
              {isEditing ? "Editar Parceiro" : "Criar Novo Parceiro"}
            </Typography>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmitPartner)}>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Basic Information */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Informações Básicas
                  </Typography>
                  <Stack spacing={2}>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nome do Parceiro"
                          error={!!errors.name}
                          helperText={errors.name?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                        <Controller
                          name="phoneNumber"
                          control={control}
                          render={({ field }) => (
                            <MuiTelInput
                              {...field}
                              label="Número WhatsApp"
                              placeholder="+351 912 345 678"
                              error={!!errors.phoneNumber}
                              helperText={errors.phoneNumber?.message}
                              required
                              fullWidth
                              defaultCountry="PT"
                              preferredCountries={['PT', 'BR', 'US', 'GB', 'FR', 'ES', 'DE', 'IT']}
                            />
                          )}
                        />
                    <Controller
                      name="IBAN"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="IBAN"
                          placeholder="PT50 1234 5678 9012 3456 7890 1"
                          error={!!errors.IBAN}
                          helperText={errors.IBAN?.message}
                          required
                          onChange={(e) => {
                            const formatted = formatIBANField(e.target.value);
                            field.onChange(formatted);
                          }}
                          fullWidth
                        />
                      )}
                    />
                    {isEditing && (
                      <Controller
                        name="isActive"
                        control={control}
                        render={({ field }) => (
                          <FormControl component="fieldset" variant="standard">
                            <FormLabel component="legend">Status</FormLabel>
                            <RadioGroup
                              row
                              aria-label="status"
                              name="isActive"
                              value={field.value}
                              onChange={field.onChange}
                            >
                              <FormControlLabel
                                value={true}
                                control={<Radio />}
                                label="Ativo"
                              />
                              <FormControlLabel
                                value={false}
                                control={<Radio />}
                                label="Inativo"
                              />
                            </RadioGroup>
                          </FormControl>
                        )}
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {/* Error Alert */}
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowPartnerDialog(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : (isEditing ? <Edit /> : <Add />)}
            >
              {submitting 
                ? (isEditing ? "Atualizando..." : "Criando...") 
                : (isEditing ? "Atualizar Parceiro" : "Criar Parceiro")
              }
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Partner Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Tem certeza que deseja excluir o parceiro "{selectedPartner?.name}"?
            Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={confirmDeletePartner}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
          >
            {deleting ? "Excluindo..." : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        message="Copiado para a área de transferência!"
      />
    </Box>
  );
}
